import React, { useState } from 'react';
import { PropTypes } from 'prop-types';
import { toastr } from 'react-redux-toastr';
import moment from 'moment';
import valueOrEmpty from 'common/App/valueOrEmpty';
import StatementTypes from 'common/Forms/StatementTypes';
import WhdValidationSummary from 'components/WhdStatement/WhdValidationSummary';
import ValidationInspector from 'common/Validation/ValidationInspector';
import AccessControl from 'components/AccessControl';
import ActivityPerms from 'components/AccessControl/permissions.json';
import { RenderDatePicker } from 'components/Common/Inputs';
import HintButton from 'components/Common/Buttons/HintButton';
import ConfirmModal from 'components/Modals/ConfirmModal';
import DateTimes from 'components/Common/DateTimes';

import { updateStatement } from 'actions';

const StatementOptionsPanel = ({ statement, structure, accountId, permissions, dispatch, validationResult, showingVersion }) => {
  const specificValidationCodes = ['REJ600070'];
  const hasValidationItems = ValidationInspector.hasRejections(validationResult, specificValidationCodes);
  const canEdit = AccessControl.hasPermission(permissions, [ActivityPerms.multiTierMgrEditWhs]) && !showingVersion;
  const isDetailed = statement && statement.type === StatementTypes.DetailedWhdStatement;
  const isPooled = statement && statement.type === StatementTypes.PooledWhdStatement;
  const isJointAccountElected = statement?.jointAccountElection && statement.jointAccountElection.toLowerCase() === 'yes';
  const canConvertJointAccountElection = (!statement?.jointAccountElection
    || statement.jointAccountElection.toLowerCase() !== 'yes')
    && (structure.recipients && structure.recipients.filter(x => x.recipients && x.recipients.length > 0).length === 0);
  const [isEdit, setIsEdit] = useState(false);
  const [options, setOptions] = useState({
    statementEffectiveDate: statement.effectiveDate,
    statementEffectiveTo: statement.effectiveTo,
    jointAccountElection: statement.jointAccountElection,
    accountingMethod: statement.accountingMethod
  });
  const [showJointAccountModal, setShowJointAccountModal] = useState(false);
  const [jointAccountHintShown, setJointAccountHintShown] = useState(false);

  const sendUpdate = () => {
    setIsEdit(false);
    setShowJointAccountModal(false);
    dispatch(updateStatement(accountId, { options }));
  };

  const validateEffectiveDateRange = () => {
    if (!options.statementEffectiveDate) {
      return false;
    }

    if (!options.statementEffectiveTo) {
      return true;
    }

    const fromMoment = moment(options.statementEffectiveDate, 'MM-DD-YYYY');
    const toMoment = moment(options.statementEffectiveTo, 'MM-DD-YYYY');

    return fromMoment.isSameOrBefore(toMoment, 'day');
  };

  const submitData = () => {
    if (!options.statementEffectiveDate) {
      toastr.error(
        'Error',
        'An effective date must be set when updating the statement.'
      );
    } else if (!validateEffectiveDateRange()) {
      toastr.error(
        'Error',
        'The effective date range selected is invalid.'
      );
    } else if (options.jointAccountElection === 'Yes' && (statement.jointAccountElection === null || statement.jointAccountElection.toLowerCase() !== 'yes')) {
      setShowJointAccountModal(true);
    } else {
      sendUpdate();
    }
  };

  const dateEffectiveFromOnChange = (e) => {
    setOptions({ ...options, statementEffectiveDate: e });
  };

  const dateEffectiveToOnChange = (e) => {
    setOptions({ ...options, statementEffectiveTo: e });
  };

  return <div className={ `panel p-2 full-height ml-2${ (hasValidationItems && ' validation-highlight') || '' }` }>
    <div className="panel-header"><h2>Statement Options</h2></div>
    <div className="panel-body pt-2 flex-columns">
      <div className="intermediary-details statement-options">
        <div className="columns">
          <div className="col-6 panel-item-name">
            Type
          </div>
          <div className="col-6">
            { StatementTypes.getDescription(statement.type) }
          </div>
        </div>
        {!isPooled && <div className="columns">
          <div className="col-6 panel-item-name">
            Accounting Method
          </div>
          {!isEdit && <div className="col-6">
            { valueOrEmpty(statement.accountingMethod) }
          </div>}
          {isEdit && <div className="col-md-4 col-6">
            <select
              className="form-select full-width"
              defaultValue={ statement.accountingMethod }
              onChange={ e => setOptions({ ...options, accountingMethod: e.target.value }) }
            >
              <option value="Ex-Date">Ex-Date</option>
              <option value="Record Date">Record Date</option>
              <option value="Payable Date">Payable Date</option>
            </select>
          </div>}
        </div> }
        <div className="columns">
          <div className="col-6 panel-item-name">
            { `Effective From${ (isEdit && ' (required)') || '' }` }
          </div>
          { !isEdit && <div className="col-6">
            { statement?.effectiveDate ? moment(statement.effectiveDate, 'MM-DD-YYYY').format('MMM DD, YYYY') : valueOrEmpty(statement.effectiveDate) }
          </div> }
          { isEdit && <div className="col-md-4 col-6"><RenderDatePicker
            input={ { onChange: e => dateEffectiveFromOnChange(e), value: options.statementEffectiveDate && options.statementEffectiveDate.length > 0 ? options.statementEffectiveDate : '' } }
            placeholder="Please select a date"
            className="form-input full-width"
            dateFormat="MM-dd-yyyy"
            maxDate={ options.statementEffectiveTo && new Date(Date.parse(options.statementEffectiveTo)) < DateTimes.today ? new Date(Date.parse(options.statementEffectiveTo)) : DateTimes.today }
            isClearable
          /></div> }
        </div>
        <div className="columns">
          <div className="col-6 panel-item-name">
            { `Effective To${ (isEdit && ' (optional)') || '' }` }
          </div>
          { !isEdit && <div className={ `col-6${ (statement && !statement.effectiveTo && ' italic') || '' }` }>
            { statement?.effectiveTo ? moment(statement.effectiveTo, 'MM-DD-YYYY').format('MMM DD, YYYY') : 'Effective until new version published' }
          </div> }
          { isEdit && <div className="col-md-4 col-6"><RenderDatePicker
            input={ { onChange: e => dateEffectiveToOnChange(e), value: options.statementEffectiveTo && options.statementEffectiveTo.length > 0 ? options.statementEffectiveTo : '' } }
            placeholder="Please select a date"
            className="form-input full-width"
            dateFormat="MM-dd-yyyy"
            minDate={ options.statementEffectiveDate && options.statementEffectiveDate.length > 0 ? new Date(Date.parse(options.statementEffectiveDate)) : new Date() }
            isClearable
          /></div> }
        </div>
        { (isDetailed || isPooled) && <div className="columns">
          <div className="col-6 panel-item-name">
            Joint Account Election
          </div>
          <div className="joint-account-elect col-6">
            { !isEdit && valueOrEmpty(statement.jointAccountElection) }
            { isEdit && <React.Fragment>
              <select
                className="form-select yes-no-select"
                disabled={ !canConvertJointAccountElection }
                defaultValue={ !statement.jointAccountElection || statement.jointAccountElection.toLowerCase() !== 'yes' ? 'No' : 'Yes' }
                onChange={ e => setOptions({ ...options, jointAccountElection: e.target.value }) }
              >
                <option value="Yes">Yes</option>
                <option value="No">No</option>
                <option value="N/A">N/A</option>
              </select>
              { (isJointAccountElected || !canConvertJointAccountElection) && <HintButton
                hintType="hint"
                active={ jointAccountHintShown }
                onClick={ () => { setJointAccountHintShown(!jointAccountHintShown); } }
              /> }
              { jointAccountHintShown && isJointAccountElected && <span className="toast toast-info">
                Structures already set with a Joint Account Election can not be changed
              </span> }
              { jointAccountHintShown && !isJointAccountElected && !canConvertJointAccountElection && <span className="toast toast-info">
                Structures with multiple layers of nesting cannot be converted into to joint account structures.
              </span> }
            </React.Fragment> }
          </div>
        </div> }
      </div>
    </div>
    <div className="panel-footer align-vertical-middle space-between m-2">
      <WhdValidationSummary sectionTitle="Statement Options" validationResult={ validationResult } specificCodes={ specificValidationCodes } className="ml-2" />
      { canEdit && <div className={ `action-buttons ${ (!validationResult || (!validationResult || !hasValidationItems)) ? 'col-12' : '' }` }>
        { !isEdit && <button className="btn btn-secondary" type="button" onClick={ () => setIsEdit(!isEdit) }><span className="ag-icon-tick" /><i className="icon icon-2x icon-edit" /></button> }
        { isEdit && <button disabled={ !options.statementEffectiveDate || options.statementEffectiveDate.length <= 0 } className="btn btn-secondary" type="button" onClick={ () => submitData() }><i className="icon icon-2x icon-check" /></button> }
        { isEdit && <button className="btn btn-secondary" type="button" onClick={ () => setIsEdit(!isEdit) }><i className="icon icon-2x icon-cross" /></button> }
      </div> }
    </div>
    <ConfirmModal
      key="jointAccountElection"
      isShown={ showJointAccountModal }
      onConfirm={ () => sendUpdate() }
      onCancel={ () => setShowJointAccountModal(false) }
      title="Joint Account Election"
      message="Are you sure that the joint account election applies to this statement? This cannot be undone."
    />
  </div>;
};

StatementOptionsPanel.propTypes = {
  statement: PropTypes.shape({
    beneficialOwners: PropTypes.arrayOf(PropTypes.shape({})),
    accountingMethod: PropTypes.string,
    effectiveDate: PropTypes.string,
    effectiveTo: PropTypes.string,
    hybridStatus: PropTypes.string,
    jointAccountElection: PropTypes.string,
    type: PropTypes.number,
  }),
  structure: PropTypes.shape(),
  validationResult: PropTypes.shape({}),
  accountId: PropTypes.string.isRequired,
  permissions: PropTypes.arrayOf(PropTypes.string),
  dispatch: PropTypes.func.isRequired,
  showingVersion: PropTypes.bool
};

StatementOptionsPanel.defaultProps = {
  permissions: null,
  statement: null,
  structure: null,
  validationResult: null,
  showingVersion: false
};

export default StatementOptionsPanel;
